import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, FileCheck, AlertCircle, Trash2, XCircle, History, Ban } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Sub-status options for "protocolo"
const SUB_STATUS_PETICIONANDO = [
  { value: 'acao_processo_protocolo', label: 'Ação em processo de protocolo' },
  { value: 'acao_protocolada', label: 'Ação protocolada' },
  { value: 'decisao_concedida', label: 'Decisão concedida' },
  { value: 'orgaos_notificados', label: 'Órgãos notificados' }
];

// Baixa items for "em_processo_baixa"
const BAIXA_ITEMS = [
  { key: 'serasa', label: 'SERASA' },
  { key: 'spc_brasil', label: 'SPC Brasil' },
  { key: 'boa_vista', label: 'SCPC Boa Vista' },
  { key: 'ieptb_nacional', label: 'IEPTB Nacional' },
  { key: 'ieptb_sp', label: 'IEPTB SP' }
];

export const StatusDetailModal = ({ client, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(client?.status || 'protocolo');
  
  // State for protocolo
  const [subStatus, setSubStatus] = useState(client?.sub_status || '');
  
  // State for em_processo_baixa
  const [baixaItems, setBaixaItems] = useState(client?.baixa_items || {
    serasa: '',
    spc_brasil: '',
    boa_vista: '',
    ieptb_nacional: '',
    ieptb_sp: ''
  });
  
  // State for concluido
  const [conclusaoTexto, setConclusaoTexto] = useState(
    client?.conclusao_texto || 'NADA CONSTA Serasa, liberado. Solicite o seu'
  );

  useEffect(() => {
    if (client) {
      setActiveTab(client.status || 'protocolo');
      setSubStatus(client.sub_status || '');
      setBaixaItems(client.baixa_items || {
        serasa: '',
        spc_brasil: '',
        boa_vista: '',
        ieptb_nacional: '',
        ieptb_sp: ''
      });
      setConclusaoTexto(client.conclusao_texto || 'NADA CONSTA Serasa, liberado. Solicite o seu');
    }
  }, [client]);

  const handleBaixaChange = (key, value) => {
    setBaixaItems(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    if (!client?.service_id) {
      toast.error('Serviço não encontrado para este cliente');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        status: activeTab,
        sub_status: activeTab === 'protocolo' ? subStatus : null,
        baixa_items: activeTab === 'em_processo_baixa' ? baixaItems : null,
        conclusao_texto: activeTab === 'concluido' ? conclusaoTexto : null
      };

      await axios.put(`${API}/services/${client.service_id}`, payload, getAuthHeaders());
      toast.success('Status atualizado com sucesso!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async () => {
    if (!window.confirm('Tem certeza que deseja remover este serviço? Esta ação não pode ser desfeita.')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/services/${client.service_id}`, getAuthHeaders());
      toast.success('Serviço removido com sucesso!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover serviço');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'protocolo':
        return <Clock className="w-4 h-4" />;
      case 'em_processo_baixa':
        return <AlertCircle className="w-4 h-4" />;
      case 'concluido':
        return <CheckCircle className="w-4 h-4" />;
      case 'revogada':
        return <XCircle className="w-4 h-4" />;
      case 'cancelado':
        return <Ban className="w-4 h-4" />;
      default:
        return <FileCheck className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'protocolo':
        return 'Protocolo';
      case 'em_processo_baixa':
        return 'Em processo de baixa';
      case 'concluido':
        return 'Concluído';
      case 'revogada':
        return 'Revogada';
      case 'cancelado':
        return 'Cancelado';
      default:
        return status;
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]" data-testid="status-detail-modal">
        <DialogHeader>
          <DialogTitle data-testid="status-detail-title">
            Gerenciar Status - {client?.nome_completo || client?.razao_social}
          </DialogTitle>
          <DialogDescription>
            Atualize o status e detalhes do processo do cliente
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5" data-testid="status-tabs">
            <TabsTrigger value="protocolo" className="flex items-center gap-1 text-xs" data-testid="tab-protocolo">
              {getStatusIcon('protocolo')}
              <span className="hidden sm:inline">A ser protocolado</span>
              <span className="sm:hidden">Protocolo</span>
            </TabsTrigger>
            <TabsTrigger value="em_processo_baixa" className="flex items-center gap-1 text-xs" data-testid="tab-baixa">
              {getStatusIcon('em_processo_baixa')}
              <span className="hidden sm:inline">Em processo de baixa</span>
              <span className="sm:hidden">Baixa</span>
            </TabsTrigger>
            <TabsTrigger value="concluido" className="flex items-center gap-1 text-xs" data-testid="tab-concluido">
              {getStatusIcon('concluido')}
              <span>Concluído</span>
            </TabsTrigger>
            <TabsTrigger value="revogada" className="flex items-center gap-1 text-xs text-red-600" data-testid="tab-revogada">
              {getStatusIcon('revogada')}
              <span>Revogada</span>
            </TabsTrigger>
            <TabsTrigger value="cancelado" className="flex items-center gap-1 text-xs text-gray-600" data-testid="tab-cancelado">
              {getStatusIcon('cancelado')}
              <span>Cancelado</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab: Peticionando (A ser protocolado) */}
          {/* Tab: Protocolo */}
          <TabsContent value="protocolo" className="space-y-4 mt-4">
            <div className="space-y-3">
              <Label>Subtítulo do Status</Label>
              <RadioGroup value={subStatus} onValueChange={setSubStatus} className="space-y-2">
                {SUB_STATUS_PETICIONANDO.map((option) => (
                  <div 
                    key={option.value} 
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <RadioGroupItem value={option.value} id={option.value} data-testid={`radio-${option.value}`} />
                    <Label htmlFor={option.value} className="cursor-pointer flex-1">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </TabsContent>

          {/* Tab: Em processo de baixa */}
          <TabsContent value="em_processo_baixa" className="space-y-4 mt-4">
            <div className="space-y-3">
              <Label>Status por Órgão</Label>
              <div className="space-y-3">
                {BAIXA_ITEMS.map((item) => (
                  <div 
                    key={item.key} 
                    className="flex items-center justify-between p-3 border rounded-lg"
                    data-testid={`baixa-item-${item.key}`}
                  >
                    <span className="font-medium text-sm">{item.label}</span>
                    <div className="flex gap-2">
                      <Select 
                        value={baixaItems[item.key] || ''} 
                        onValueChange={(value) => handleBaixaChange(item.key, value)}
                      >
                        <SelectTrigger className="w-[140px]" data-testid={`select-${item.key}`}>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="baixando">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                              BAIXANDO
                            </span>
                          </SelectItem>
                          <SelectItem value="baixado">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              BAIXADO
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Summary badges */}
              <div className="flex flex-wrap gap-2 pt-2">
                {Object.entries(baixaItems).map(([key, value]) => {
                  if (!value) return null;
                  const item = BAIXA_ITEMS.find(b => b.key === key);
                  return (
                    <Badge 
                      key={key} 
                      variant={value === 'baixado' ? 'default' : 'secondary'}
                      className={value === 'baixado' ? 'bg-green-600' : 'bg-yellow-600'}
                    >
                      {item?.label}: {value.toUpperCase()}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Tab: Concluído */}
          <TabsContent value="concluido" className="space-y-4 mt-4">
            <div className="space-y-3">
              <Label htmlFor="conclusao">Mensagem de Conclusão</Label>
              <Textarea
                id="conclusao"
                value={conclusaoTexto}
                onChange={(e) => setConclusaoTexto(e.target.value)}
                placeholder="Ex: NADA CONSTA Serasa, liberado. Solicite o seu"
                className="min-h-[100px]"
                data-testid="conclusao-textarea"
              />
              <p className="text-xs text-muted-foreground">
                Esta mensagem será exibida quando o processo estiver concluído
              </p>
            </div>
          </TabsContent>

          {/* Tab: Revogada */}
          <TabsContent value="revogada" className="space-y-4 mt-4">
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <XCircle className="w-5 h-5" />
                <span className="font-medium">Processo Revogado</span>
              </div>
              <p className="text-sm text-red-600 dark:text-red-300 mt-2">
                Este status indica que o processo foi revogado e não terá continuidade.
              </p>
            </div>
          </TabsContent>

          {/* Tab: Cancelado */}
          <TabsContent value="cancelado" className="space-y-4 mt-4">
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-testid="cancelado-info-block">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <Ban className="w-5 h-5" />
                <span className="font-medium">Processo Cancelado</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Este status indica que o processo foi cancelado. Diferente de "Revogada", o cancelamento pode ocorrer por desistência do cliente, erro no cadastro ou outra razão administrativa.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Current status indicator */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status atual:</span>
            <Badge variant="outline" className="flex items-center gap-1">
              {getStatusIcon(activeTab)}
              {getStatusLabel(activeTab)}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeleteService}
            disabled={loading}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="delete-service-button"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Remover Serviço
          </Button>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            data-testid="status-cancel-button"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="flex-1"
            data-testid="status-save-button"
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatusDetailModal;
